/* Proto « Atterrissage » (module Ventes) — modèle + helpers PURS.
   On travaille D'ABORD dans le CA : lignes de revenus FINANCE (loadFinanceRefs),
   complétées de sous-lignes de DÉTAIL. Un détail peut porter un INDUCTEUR
   (byDriver : driver + prix + qté) → sa valeur = qté × prix, et il apparaît dans la
   table Inducteurs (sous le CA). Sinon le détail se saisit directement en kCHF.
   Vérité mensuelle = 12 valeurs ; totaux (Réalisé, année) en dérivent.
   Persistance localStorage (proto). ⚠️ Le Réalisé devra venir de Finance (compta). */

export const isMonthlyYear = (model, y) => y === model.currentYear || y === model.currentYear + 1;
export const closedOf = (model, y) => (y === model.currentYear ? (model.closedMonths || 0) : 0);
export const yearsOf = (model) => Array.from({ length: model.nbYears }, (_, i) => model.currentYear + i);
export const zeros12 = () => Array(12).fill(0);

let idc = 0;
export const newId = () => `atl_${Date.now().toString(36)}_${(idc++).toString(36)}`;

export const spread = (total, n) => {
  if (n <= 0) return [];
  const t = Number(total) || 0;
  const base = t / n;
  const arr = Array(n).fill(base);
  arr[n - 1] = t - base * (n - 1);
  return arr;
};

/* — Lecture sur une ENTRÉE {months, annual} (détail manuel OU ligne directe) — */
export const monthsOf = (e, y) => (e && e.months && e.months[y]) || zeros12();
export const annualOf = (model, e, y) =>
  isMonthlyYear(model, y)
    ? monthsOf(e, y).reduce((s, x) => s + (Number(x) || 0), 0)
    : (Number(e?.annual?.[y]) || 0);
export const realizedOf = (model, e) =>
  monthsOf(e, model.currentYear).slice(0, closedOf(model, model.currentYear)).reduce((s, x) => s + (Number(x) || 0), 0);

/* — Détail porteur d'inducteur — deux types (dmode) :
   · 'unit' (défaut) : revenu = prix (CHF) × qté / 1000 (kCHF)
   · 'pct'           : revenu = pct% × total de la ligne de base (hors % → pas de récursion) */
export const isDriverDetail = (dd) => !!dd && !!dd.byDriver;
export const isPctDetail = (dd) => isDriverDetail(dd) && dd.dmode === 'pct';
export const driverAnnual = (dd, y) => (Number(dd.prix) || 0) * (Number(dd.qty?.[y]) || 0) / 1000;
export const qtyFromCa = (dd, ca) => (Number(dd.prix) > 0 ? (Number(ca) || 0) * 1000 / Number(dd.prix) : 0);

/* Base d'un % = total de la ligne référencée HORS détails % (stoppe la récursion). */
export const lineBase = (model, id, y) => {
  const ds = detailsOf(model, id);
  return ds.length ? ds.reduce((s, dd) => s + (dd.dmode === 'pct' ? 0 : detailAnnual(model, dd, y)), 0) : annualOf(model, entryOf(model, id), y);
};
// Type % : `prix` porte le POURCENTAGE (rate unique) ; base = ligne référencée.
export const detailAnnual = (model, dd, y) => {
  if (!isDriverDetail(dd)) return annualOf(model, dd, y);
  if (dd.dmode === 'pct') return Math.round((Number(dd.prix) || 0) / 100 * lineBase(model, dd.baseLineId, y));
  // Plan mensuel (qtyM) prioritaire : annuel = réalisé (année en cours) + Σ mois plan.
  const qm = dd.qtyM && dd.qtyM[y];
  if (qm) {
    const c = closedOf(model, y);
    const fore = Object.keys(qm).reduce((s, m) => (Number(m) >= c ? s + (Number(dd.prix) || 0) * (Number(qm[m]) || 0) / 1000 : s), 0);
    return (y === model.currentYear ? driverRealized(dd) : 0) + fore;
  }
  return driverAnnual(dd, y);
};
/* Réalisé d'un inducteur unité : le CA SAISI est la donnée stockée (jamais
   recalculé — changer le prix ne touche pas le réel, seul le volume déduit bouge).
   · caRealM = {mois: kCHF} — réalisé MENSUEL saisi en CA
   · caReal  = kCHF annuel (saisie du total réalisé) — utilisé si pas de mensuel.
   Le prévu (futur) reste volume × prix. */
const kOf = (dd, q) => (Number(dd.prix) || 0) * (Number(q) || 0) / 1000;
export const driverRealized = (dd) => (dd.caRealM
  ? Object.values(dd.caRealM).reduce((s, ca) => s + (Number(ca) || 0), 0)
  : (Number(dd.caReal) || 0));
const dMonths = (model, dd, y) => {
  if (!isDriverDetail(dd)) return monthsOf(dd, y);
  if (isPctDetail(dd)) return spread(detailAnnual(model, dd, y), 12);
  const qm = dd.qtyM && dd.qtyM[y];
  if (y === model.currentYear) {
    const c = closedOf(model, y); const op = 12 - c;
    const real = driverRealized(dd);
    const arr = zeros12();
    if (dd.caRealM) { for (let i = 0; i < c; i++) arr[i] = Number(dd.caRealM[i]) || 0; }
    else { const rp = spread(real, c); for (let i = 0; i < c; i++) arr[i] = rp[i]; }
    if (qm) { for (let i = c; i < 12; i++) arr[i] = kOf(dd, qm[i]); }
    else { const fp = spread(Math.max(0, detailAnnual(model, dd, y) - real), op); for (let i = 0; i < op; i++) arr[c + i] = fp[i]; }
    return arr;
  }
  if (qm) return Array.from({ length: 12 }, (_, i) => kOf(dd, qm[i]));
  return spread(detailAnnual(model, dd, y), 12);
};
/* Plan mensuel d'un inducteur : saisir la QTÉ d'un mois ouvert (ou son CA converti
   en qté côté page). À la 1re saisie, fige les autres mois à leur valeur courante. */
export const setDriverPlanMonth = (d, lineId, did, y, m, qty) => {
  const dd = findDetail(d, lineId, did); if (!dd) return;
  if (!dd.qtyM) dd.qtyM = {};
  if (!dd.qtyM[y]) {
    const c = y === d.currentYear ? closedOf(d, y) : 0;
    const init = {};
    for (let i = c; i < 12; i++) init[i] = qtyFromCa(dd, Number(dMonths(d, dd, y)[i]) || 0);
    dd.qtyM[y] = init;
  }
  dd.qtyM[y][m] = Number(qty) || 0;
};
/* Saisie du CA réalisé d'UN MOIS : stocké TEL QUEL (le volume s'en déduit). */
export const setDriverRealMonth = (d, lineId, did, m, ca) => {
  const dd = findDetail(d, lineId, did); if (!dd) return;
  if (!dd.caRealM) {
    // 1re saisie mensuelle : éclate le total existant en mensuel (linéaire).
    dd.caRealM = {};
    const c = closedOf(d, d.currentYear);
    const part = spread(driverRealized(dd), c);
    for (let i = 0; i < c; i++) dd.caRealM[i] = part[i];
  }
  dd.caRealM[m] = Number(ca) || 0;
};
/* Saisie du total réalisé (annuel) : stocké tel quel, repart de zéro en mensuel. */
export const setDriverRealTotal = (d, lineId, did, ca) => {
  const dd = findDetail(d, lineId, did); if (!dd) return;
  dd.caReal = Number(ca) || 0;
  delete dd.caRealM; delete dd.qtyReal; delete dd.qtyRealM;
};
export const detailRealized = (model, dd) =>
  (isDriverDetail(dd) && !isPctDetail(dd))
    ? driverRealized(dd)
    : dMonths(model, dd, model.currentYear).slice(0, closedOf(model, model.currentYear)).reduce((s, x) => s + (Number(x) || 0), 0);
export const detailMonth = (model, dd, y, m) => Number(dMonths(model, dd, y)[m]) || 0;

/* — Niveau LIGNE Finance : Σ détails (ou saisie directe si aucun détail) — */
export const entryOf = (model, id) => (model.data && model.data[id]) || { months: {}, annual: {}, details: [] };
export const detailsOf = (model, id) => entryOf(model, id).details || [];

export const lineAnnual = (model, id, y) => {
  const ds = detailsOf(model, id);
  return ds.length ? ds.reduce((s, dd) => s + detailAnnual(model, dd, y), 0) : annualOf(model, entryOf(model, id), y);
};
export const lineRealized = (model, id) => {
  const ds = detailsOf(model, id);
  return ds.length ? ds.reduce((s, dd) => s + detailRealized(model, dd), 0) : realizedOf(model, entryOf(model, id));
};
export const lineMonth = (model, id, y, m) => {
  const ds = detailsOf(model, id);
  return ds.length ? ds.reduce((s, dd) => s + detailMonth(model, dd, y, m), 0) : (Number(monthsOf(entryOf(model, id), y)[m]) || 0);
};

/* — REVENUS VARIABLES (card Variables) : une ligne cible = taux% × une ligne de base.
   Concept distinct des inducteurs. Taux futur saisi par année ; taux réel observé
   (calculé sur la compta) côté page. Top-level (pas un détail) → pas de récursion :
   la base est lue via lineAnnual (détails only, les variables n'y entrent pas). */
export const variablesOf = (model) => model.variables || [];
/* baseRef d'un variable : 'line:<id>' (total ligne) · 'det:<lineId>:<did>' (un
   détail) · 'adj:<lineId>' (Alignement Finance de la ligne). L'évaluation de la
   base vit côté page (elle nécessite l'overlay compta Finance). */
export const parseRef = (ref) => {
  const p = String(ref || '').split(':');
  if (p[0] === 'det') return { kind: 'det', lineId: p[1], did: p[2] };
  if (p[0] === 'adj') return { kind: 'adj', lineId: p[1] };
  return { kind: 'line', lineId: p[1] || '' };
};
/* targetRef : 'line:<id>' ou 'det:<lineId>:<did>' — la contribution alimente
   toujours la LIGNE de la cible (le détail sert de rattachement fin). */
export const addVariable = (d, targetRef, baseRef) => {
  const rate = {}; yearsOf(d).forEach((y) => { rate[y] = 50; });
  (d.variables = d.variables || []).push({ id: newId(), name: '', targetRef, baseRef, rate });
};
export const delVariable = (d, id) => { d.variables = (d.variables || []).filter((x) => x.id !== id); };
export const setVariableField = (d, id, field, v) => { const vv = (d.variables || []).find((x) => x.id === id); if (vv) vv[field] = v; };
export const setVariableRate = (d, id, y, v) => { const vv = (d.variables || []).find((x) => x.id === id); if (vv) { if (!vv.rate) vv.rate = {}; vv.rate[y] = Number(v) || 0; } };

/* Tous les inducteurs = détails byDriver, à plat, pour la table Inducteurs. */
export const allDrivers = (model) => {
  const out = []; const data = model.data || {};
  Object.keys(data).forEach((lineId) => (data[lineId].details || []).forEach((dd) => { if (dd.byDriver) out.push({ lineId, did: dd.id, dd }); }));
  return out;
};

/* — Mutations (draft mutable `d`) — */
const withFields = (e) => { if (!e.months) e.months = {}; if (!e.annual) e.annual = {}; return e; };
export const lineEntry = (d, id) => {
  if (!d.data) d.data = {};
  if (!d.data[id]) d.data[id] = { months: {}, annual: {}, details: [] };
  const e = d.data[id]; if (!e.details) e.details = [];
  return withFields(e);
};
export const findDetail = (d, id, did) => lineEntry(d, id).details.find((x) => x.id === did);
export const detailEntry = (d, id, did) => withFields(findDetail(d, id, did) || {});
/* Plan manuel de la ligne « Autres <ligne> » (écart compta) : le réalisé reste
   calculé (compta − détails), le FUTUR se planifie ici. */
export const adjEntry = (d, id) => {
  const e = lineEntry(d, id);
  if (!e.adjPlan) e.adjPlan = { months: {}, annual: {} };
  return withFields(e.adjPlan);
};
export const adjPlanOf = (model, id) => (entryOf(model, id).adjPlan) || { months: {}, annual: {} };
export const addDetail = (d, id, name = '') => { const dd = { id: newId(), name, months: {}, annual: {} }; lineEntry(d, id).details.push(dd); return dd.id; };
export const delDetail = (d, id, did) => { const e = lineEntry(d, id); e.details = e.details.filter((x) => x.id !== did); };
export const renameDetail = (d, id, did, name) => { const dd = findDetail(d, id, did); if (dd) dd.name = name; };

/* Rattacher / retirer un inducteur à un détail (jours-hommes · 1400 CHF · 10 j). */
export const attachDriver = (d, id, did, driverName) => {
  const dd = findDetail(d, id, did); if (!dd) return;
  dd.byDriver = true; dd.dmode = 'unit';
  dd.driver = driverName != null ? driverName : (dd.driver || 'jours-hommes');
  if (dd.prix == null) dd.prix = 1400;
  // CA manuel déjà saisi → CONVERTI en volumes (rien n'est effacé) : mois clos →
  // qtyRealM, mois ouverts → qtyM, années annuelles → qty. dd.months est conservé
  // (ignoré tant que l'inducteur est actif) pour un éventuel retrait.
  const cy = d.currentYear; const c = closedOf(d, cy);
  const hadCa = (dd.months && Object.keys(dd.months).length) || (dd.annual && Object.keys(dd.annual).length);
  if (hadCa) {
    dd.qty = dd.qty || {};
    [cy, cy + 1].forEach((y) => {
      const ms = dd.months && dd.months[y];
      if (!ms) return;
      const cc = y === cy ? c : 0;
      if (cc > 0) { dd.caRealM = {}; for (let i = 0; i < cc; i++) dd.caRealM[i] = Number(ms[i]) || 0; }
      const qm = {}; for (let i = cc; i < 12; i++) qm[i] = qtyFromCa(dd, ms[i]);
      (dd.qtyM = dd.qtyM || {})[y] = qm;
    });
    if (dd.annual) Object.keys(dd.annual).forEach((y) => { dd.qty[y] = qtyFromCa(dd, dd.annual[y]); });
  }
  if (!dd.qty) { dd.qty = {}; yearsOf(d).forEach((y) => { dd.qty[y] = 10; }); }
};
/* Inducteur % : base = une AUTRE ligne de revenus ; `prix` porte le taux (%). */
export const attachPct = (d, id, did, baseLineId = '') => {
  const dd = findDetail(d, id, did); if (!dd) return;
  dd.byDriver = true; dd.dmode = 'pct'; dd.baseLineId = baseLineId; dd.prix = 50;
};
export const detachDriver = (d, id, did) => { const dd = findDetail(d, id, did); if (dd) dd.byDriver = false; };
export const setDetailField = (d, id, did, field, v) => { const dd = findDetail(d, id, did); if (dd) dd[field] = v; };
export const setDetailQty = (d, id, did, y, v) => { const dd = findDetail(d, id, did); if (dd) { if (!dd.qty) dd.qty = {}; dd.qty[y] = Number(v) || 0; if (dd.qtyM) delete dd.qtyM[y]; } };

/* Répartit un total sur arr[start..end) : vide → linéaire ; sinon ajuste le dernier. */
const distributeInto = (arr, start, end, target) => {
  const n = end - start; if (n <= 0) return;
  const sum = arr.slice(start, end).reduce((s, x) => s + (Number(x) || 0), 0);
  if (sum === 0) {
    const part = spread(target, n);
    for (let i = start; i < end; i++) arr[i] = part[i - start];
  } else {
    arr[end - 1] = (Number(arr[end - 1]) || 0) + ((Number(target) || 0) - sum);
  }
};
export const setAnnual = (d, e, y, v) => {
  withFields(e);
  if (isMonthlyYear(d, y)) {
    const c = y === d.currentYear ? closedOf(d, y) : 0;
    const arr = (e.months[y] || zeros12()).slice();
    if (c > 0) {
      const realized = arr.slice(0, c).reduce((s, x) => s + (Number(x) || 0), 0);
      distributeInto(arr, c, 12, (Number(v) || 0) - realized);
    } else {
      distributeInto(arr, 0, 12, Number(v) || 0);
    }
    e.months[y] = arr;
  } else {
    e.annual[y] = Number(v) || 0;
  }
};
export const setRealized = (d, e, v) => {
  const c = closedOf(d, d.currentYear);
  if (c <= 0) return;
  withFields(e);
  const arr = (e.months[d.currentYear] || zeros12()).slice();
  distributeInto(arr, 0, c, Number(v) || 0);
  e.months[d.currentYear] = arr;
};
/* RÈGLE DE PROJECTION mémorisée sur l'entrée (chip affiché sur la ligne).
   Elle POSSÈDE les mois ouverts : tant qu'elle est armée, ils se recalculent à
   chaque nouveau mois de compta chargé (2026-08-19 — auparavant la règle
   collait une fois les valeurs et périmait au mois suivant). Saisir un mois
   ouvert à la main la désarme : la main l'emporte, les valeurs restent. */
export const setEntryRule = (e, ruleId) => { if (e) e.rule = ruleId || null; };
export const entryRule = (e) => (e && e.rule) || null;
/* Valeur projetée par une règle, à partir des mois RÉELS (clos) de l'année. */
export const ruleValue = (ruleId, realVals) => {
  const n = (realVals || []).length;
  if (ruleId === 'last') return n ? Number(realVals[n - 1]) || 0 : 0;
  if (ruleId === 'avg') return n ? realVals.reduce((s, x) => s + (Number(x) || 0), 0) / n : 0;
  return 0;
};
/* Écrit les mois ouverts depuis la règle et (ré)arme celle-ci. Unique chemin
   d'application — utilisé par le chip ET par le recalcul automatique. */
export const applyEntryRule = (d, e, y, closed, realVals, ruleId) => {
  const v = ruleValue(ruleId, realVals);
  for (let m = closed; m < 12; m++) setMonth(d, e, y, m, v);
  setEntryRule(e, ruleId);
};

export const setMonth = (d, e, y, m, v) => {
  withFields(e);
  const arr = (e.months[y] || zeros12()).slice();
  arr[m] = Number(v) || 0;
  e.months[y] = arr;
  // Saisie manuelle sur un mois OUVERT : la règle ne possède plus la ligne.
  // (Les chemins qui appliquent une règle la réarment juste après.)
  if (m >= closedOf(d, y)) e.rule = null;
};

/* Copie l'année en cours sur N+1, ligne par ligne (sélection) :
   · détail manuel / saisie directe → profil mensuel copié tel quel
   · détail à inducteur → VOLUMES mensuels copiés (qtyM[N+1]), prix inchangé
   · détail % (legacy) → rien (suit sa base)
   · variables ciblant la ligne → taux N+1 = taux N. */
export const copyYearForward = (d, lineIds) => {
  const from = d.currentYear, to = from + 1;
  lineIds.forEach((id) => {
    const e = lineEntry(d, id);
    if (e.details.length) {
      e.details.forEach((dd) => {
        if (isPctDetail(dd)) return;
        if (dd.byDriver) {
          const qm = {};
          for (let m = 0; m < 12; m++) qm[m] = qtyFromCa(dd, Number(dMonths(d, dd, from)[m]) || 0);
          (dd.qtyM = dd.qtyM || {})[to] = qm;
        } else {
          if (!dd.months) dd.months = {};
          dd.months[to] = monthsOf(dd, from).slice();
        }
      });
    } else {
      e.months[to] = monthsOf(e, from).slice();
    }
  });
  (d.variables || []).forEach((v) => {
    if (lineIds.includes(parseRef(v.targetRef).lineId) && v.rate && v.rate[from] != null) v.rate[to] = v.rate[from];
  });
  // Les cards Plan PRODUITS / SERVICES attachées aux lignes sélectionnées
  // copient aussi leur année : volumes mensuels (le réel jan-juin devient un
  // plan), niveaux tenus, % du mensualisé, facturations et % projet.
  const copyRowYear = (r) => {
    if (r.qtyM && r.qtyM[from]) r.qtyM[to] = { ...r.qtyM[from] };
    else if (r.qty && r.qty[from] != null) { if (!r.qty) r.qty = {}; r.qty[to] = r.qty[from]; }
    if (r.pctM && r.pctM[from]) { r.pctM[to] = { ...r.pctM[from] }; }
    if (r.volume && r.volume[from] != null) r.volume[to] = r.volume[from];
    if (r.billedM && r.billedM[from]) { r.billedM[to] = { ...r.billedM[from] }; }
    if (r.pct && r.pct[from]) { r.pct[to] = { ...r.pct[from] }; }
    if (r.caM && r.caM[from]) r.caM[to] = { ...r.caM[from] };
    else if (r.ca && r.ca[from] != null) r.ca[to] = r.ca[from];
  };
  [...productPlanOf(d), ...servicePlanOf(d)]
    .filter((c) => lineIds.includes(c.lineId))
    .forEach((c) => (c.rows || []).forEach(copyRowYear));
};

export const defaultModel = (currentYear = 2026) => ({
  currentYear: Number(currentYear) || 2026, nbYears: 6, closedMonths: 6, data: {}, variables: [],
});
export const sanitize = (model, currentYear = 2026) => {
  const m = model && typeof model === 'object' ? model : {};
  const cy = m.currentYear || Number(currentYear) || 2026;
  const nb = m.nbYears || 6;
  const data = m.data && typeof m.data === 'object' ? m.data : {};
  const variables = Array.isArray(m.variables) ? m.variables.slice() : [];
  // Migration : anciens détails % → revenus variables (card Variables).
  Object.keys(data).forEach((lineId) => {
    const e = data[lineId];
    if (e && Array.isArray(e.details)) {
      e.details.filter((dd) => dd.dmode === 'pct').forEach((dd) => {
        const rate = {}; for (let i = 0; i < nb; i++) rate[cy + i] = Number(dd.prix) || 0;
        variables.push({ id: dd.id, name: dd.name || '', targetRef: `line:${lineId}`, baseRef: dd.baseLineId ? `line:${dd.baseLineId}` : '', rate });
      });
      e.details = e.details.filter((dd) => dd.dmode !== 'pct');
    }
  });
  // Migration : réalisé d'inducteur stocké en VOLUME (qtyReal/qtyRealM) → CA
  // (caReal/caRealM), converti une fois au prix courant. Ensuite le CA est figé.
  Object.keys(data).forEach((lineId) => {
    ((data[lineId] || {}).details || []).forEach((dd) => {
      if (!dd.byDriver) return;
      const px = Number(dd.prix) || 0;
      if (dd.qtyRealM && !dd.caRealM) { dd.caRealM = {}; Object.keys(dd.qtyRealM).forEach((m) => { dd.caRealM[m] = px * (Number(dd.qtyRealM[m]) || 0) / 1000; }); delete dd.qtyRealM; }
      if (dd.qtyReal != null && dd.caReal == null) { dd.caReal = px * (Number(dd.qtyReal) || 0) / 1000; delete dd.qtyReal; }
    });
  });
  // Migration : baseLineId → baseRef · targetLineId → targetRef.
  variables.forEach((v) => {
    if (!v.baseRef && v.baseLineId) v.baseRef = `line:${v.baseLineId}`;
    if (!v.targetRef && v.targetLineId) v.targetRef = `line:${v.targetLineId}`;
  });
  return { currentYear: cy, nbYears: nb, closedMonths: m.closedMonths == null ? 6 : m.closedMonths, data, variables,
    productPlan: (m.productPlan && Array.isArray(m.productPlan.cards)) ? m.productPlan : { cards: [] },
    servicePlan: (m.servicePlan && Array.isArray(m.servicePlan.cards)) ? m.servicePlan : { cards: [] } };
};

/* — PLAN PRODUITS — une card = une CATÉGORIE de produit (ex. montres,
   Mouvements, Voitures) : un COMPTE de CA Finance attribué (lineId) et des
   PRODUITS (ses modèles) portant prix unitaire, coût de revient et
   volumes — annuels (qty), mensualisés sur les années mensuelles (qtyM, la 1re
   saisie fige les autres mois). Le volume se compte en UNITÉS du produit : pas
   d'inducteur à nommer (2026-08-12) — le produit EST l'inducteur.

   Le CA de la catégorie (Σ volumes × prix) s'inscrit dans son compte via la
   jointure revenueByLine ; les coûts (Σ volumes × CR) alimentent cogsByLine
   (chaîne coût des produits vendus). */
export const productPlanOf = (model) => (model.productPlan && model.productPlan.cards) || [];
export const findProductCard = (d, cid) => productPlanOf(d).find((c) => c.id === cid);
const findProductRow = (d, cid, rid) => {
  const c = findProductCard(d, cid);
  return c && c.rows.find((x) => x.id === rid);
};
export const addProductCard = (d, kind) => {
  if (!d.productPlan) d.productPlan = { cards: [] };
  // `terms` = conditions de paiement (jours) — champ enregistré ; la
  // connexion aux créances viendra du lot Finance.
  const c = { id: newId(), name: '', lineId: '', terms: 30, rows: [] };
  if (kind === 'trade') c.kind = 'trade';   // négoce sans stock — sans kind = unités
  d.productPlan.cards.push(c);
  return c;
};
export const delProductCard = (d, cid) => {
  if (d.productPlan) d.productPlan.cards = productPlanOf(d).filter((c) => c.id !== cid);
};
export const setProductCardField = (d, cid, field, v) => {
  const c = findProductCard(d, cid);
  if (c) c[field] = v;
};
export const addProductRow = (d, cid) => {
  const c = findProductCard(d, cid);
  if (!c) return null;
  const r = c.kind === 'trade'
    ? { id: newId(), name: '', mode: 'manual', margin: 0, lag: 0, pctLineId: '', pctRate: 0, ca: {}, caM: {} }
    : { id: newId(), name: '', price: 0, cost: 0, qty: {}, qtyM: {} };
  c.rows.push(r);
  return r;
};
export const delProductRow = (d, cid, rid) => {
  const c = findProductCard(d, cid);
  if (c) c.rows = c.rows.filter((r) => r.id !== rid);
};
export const setProductRowField = (d, cid, rid, field, v) => {
  const r = findProductRow(d, cid, rid);
  if (r) r[field] = v;
};
/* Volume d'un mois : plan mensuel (qtyM) prioritaire, sinon annuel réparti. */
export const productRowQtyMonth = (r, y, m) => {
  const qm = r.qtyM && r.qtyM[y];
  if (qm && qm[m] != null) return Number(qm[m]) || 0;
  return (Number(r.qty && r.qty[y]) || 0) / 12;
};
export const productRowQtyYear = (r, y) => {
  const qm = r.qtyM && r.qtyM[y];
  if (qm) return Object.values(qm).reduce((s, q) => s + (Number(q) || 0), 0);
  return Number(r.qty && r.qty[y]) || 0;
};
export const setProductRowQtyYear = (d, cid, rid, y, v) => {
  const r = findProductRow(d, cid, rid);
  if (!r) return;
  if (!r.qty) r.qty = {};
  r.qty[y] = Number(v) || 0;
  if (r.qtyM) delete r.qtyM[y];       // l'annuel reprend la main
};
export const setProductRowQtyMonth = (d, cid, rid, y, m, v, closed = 0) => {
  const r = findProductRow(d, cid, rid);
  if (!r) return;
  if (!r.qtyM) r.qtyM = {};
  if (!r.qtyM[y]) {
    const init = {};
    for (let i = closed; i < 12; i++) init[i] = productRowQtyMonth(r, y, i);
    r.qtyM[y] = init;                  // 1re saisie mensuelle : fige les autres mois
  }
  r.qtyM[y][m] = Number(v) || 0;
};
/* CA d'UNE catégorie sur un mois (kCHF) — le Plan des ventes s'en sert pour
   MONTRER la contribution de chaque catégorie sous la ligne de CA. */
export const productCardRevenueMonth = (model, card, y, m) =>
  card.kind === 'trade'
    ? (card.rows || []).reduce((t, r) => t + tradeRowCaMonth(model, card, r, y, m), 0)
    : (card.rows || []).reduce((t, r) => t + productRowQtyMonth(r, y, m) * productRowPriceAt(model, r, y) / 1000, 0);

/* — Modèle NÉGOCE (kind 'trade') — achat-revente SANS stock (HW, SW, licences
   achetés pour les clients). Une ligne = une FAMILLE planifiée en kCHF de CA.
   Marge de négoce `margin` (% du prix de vente) → achat = CA × (1 − marge).
   Décalage `lag` (M−0..M−3) : l'achat est COMPTABILISÉ lag mois AVANT la vente ;
   en fin d'année il lit l'année suivante du plan. Les achats partent
   DIRECTEMENT sur la ligne Achats du P&L (computed.tradePurchases) — ils ne
   passent ni par cogsByLine ni par le circuit stock.

   RÈGLE DE CALCUL (`r.mode`, décision Olivier 2026-08-14) : une règle ÉCRIT les
   mois ouverts, elle ne les VERROUILLE pas. On pose une base (moyenne, objectif,
   saisonnalité…) puis on l'ajuste mois par mois — typiquement le vendeur corrige
   les 3 prochains mois d'après son carnet de commandes. Il n'y a donc qu'une
   seule vérité de lecture : les valeurs de `caM`. `r.mode` ne sert qu'à se
   souvenir de la dernière règle appliquée (affichage de la pastille) et à la
   rejouer. Les mois CLOS ne sont jamais réécrits : ce sont les vrais. */
export const tradeRowCaMonth = (model, card, r, y, m) => {
  const mm = r.caM && r.caM[y];
  if (mm && mm[m] != null) return Number(mm[m]) || 0;
  return (Number(r.ca && r.ca[y]) || 0) / 12;
};

export const TRADE_MODES = ['manual', 'last', 'avg', 'target', 'n1', 'pct'];

/* Indicateurs de planification d'une ligne (kCHF) : ce qu'on lit AVANT de
   choisir une règle — cumul réel, moyenne du réel, dernier mois réel, ce qui
   reste à faire pour tenir l'objectif. */
export const tradeRowStats = (model, card, r, y, closed) => {
  const cc = Math.min(12, Math.max(0, Number(closed) || 0));
  const val = (m) => tradeRowCaMonth(model, card, r, y, m);
  let ytdSum = 0;
  for (let m = 0; m < cc; m++) ytdSum += val(m);
  let foreSum = 0;
  for (let m = cc; m < 12; m++) foreSum += val(m);
  const target = Number(r.target) || 0;
  return {
    closed: cc,
    ytdSum,
    ytdAvg: cc ? ytdSum / cc : 0,
    last: cc ? val(cc - 1) : 0,
    foreSum,
    total: ytdSum + foreSum,
    target,
    // Manco : ce qu'il reste à faire sur les mois ouverts pour tenir l'objectif.
    gap: target ? target - ytdSum - foreSum : 0,
  };
};

/* Profil de saisonnalité N-1 disponible ? (sinon la règle n1 est indisponible —
   on n'invente pas un profil de repli.) */
export const tradeRowHasN1 = (model, card, r, y) => {
  for (let m = 0; m < 12; m++) if (tradeRowCaMonth(model, card, r, y - 1, m)) return true;
  return false;
};

/* Applique la règle : ÉCRIT les mois ouverts (> clôture) de l'année. Renvoie le
   nombre de mois écrits — 0 si la règle manque d'une donnée (aucun repli
   silencieux : l'écran le dit et ne remplit rien). */
export const applyTradeRule = (d, cid, rid, y, closed, rule = {}) => {
  const c = findProductCard(d, cid);
  const r = c && c.rows.find((x) => x.id === rid);
  if (!r) return 0;
  const mode = TRADE_MODES.includes(rule.mode) ? rule.mode : 'manual';
  r.mode = mode;
  if (rule.target != null) r.target = Number(rule.target) || 0;
  if (rule.rate != null) r.pctRate = Number(rule.rate) || 0;
  if (rule.lineId != null) r.pctLineId = rule.lineId;
  if (mode === 'manual') return 0;          // la saisie reprend la main, telle quelle

  const cc = Math.min(12, Math.max(0, Number(closed) || 0));
  const open = [];
  for (let m = cc; m < 12; m++) open.push(m);
  if (!open.length) return 0;
  const st = tradeRowStats(d, c, r, y, cc);
  let series = null;

  if (mode === 'last') {
    if (!cc) return 0;
    series = open.map(() => st.last);
  } else if (mode === 'avg') {
    if (!cc) return 0;
    series = open.map(() => st.ytdAvg);
  } else if (mode === 'target') {
    // Objectif annuel : le reste à faire, réparti à parts égales sur les mois
    // ouverts (répartition prévisible — l'ajustement au mois suit).
    if (!st.target) return 0;
    const rest = (st.target - st.ytdSum) / open.length;
    series = open.map(() => rest);
  } else if (mode === 'n1') {
    // Saisonnalité N-1 : profil des mois de l'an dernier × facteur d'évolution
    // (cumul réel N / même cumul N-1). Sans base N-1 comparable → rien.
    const prev = Array.from({ length: 12 }, (_, m) => tradeRowCaMonth(d, c, r, y - 1, m));
    let ytdPrev = 0;
    for (let m = 0; m < cc; m++) ytdPrev += prev[m];
    if (!ytdPrev) return 0;
    const factor = st.ytdSum / ytdPrev;
    series = open.map((m) => prev[m] * factor);
  } else if (mode === 'pct') {
    // % du CA PLANIFIÉ d'une ligne de prestations (Plan services — pas de
    // récursion possible vers une autre card négoce).
    if (!r.pctLineId) return 0;
    const rate = (Number(r.pctRate) || 0) / 100;
    if (!rate) return 0;
    series = open.map((m) => servicePlanRevenueMonth(d, r.pctLineId, y, m) * rate);
  }
  if (!series) return 0;

  if (!r.caM) r.caM = {};
  if (!r.caM[y]) {
    const init = {};
    for (let m = 0; m < cc; m++) init[m] = tradeRowCaMonth(d, c, r, y, m);
    r.caM[y] = init;                        // fige les mois clos avant d'écrire
  }
  open.forEach((m, i) => { r.caM[y][m] = Math.round(series[i] * 1000) / 1000; });
  return open.length;
};
export const tradeRowCaYear = (model, card, r, y) =>
  Array.from({ length: 12 }, (_, m) => tradeRowCaMonth(model, card, r, y, m)).reduce((s, v) => s + v, 0);
export const setTradeRowCaYear = (d, cid, rid, y, v) => {
  const r = findProductRow(d, cid, rid);
  if (!r) return;
  if (!r.ca) r.ca = {};
  r.ca[y] = Number(v) || 0;
  if (r.caM) delete r.caM[y];        // l'annuel reprend la main
};
export const setTradeRowCaMonth = (d, cid, rid, y, m, v, closed = 0) => {
  const c = findProductCard(d, cid);
  const r = c && c.rows.find((x) => x.id === rid);
  if (!r) return;
  if (!r.caM) r.caM = {};
  if (!r.caM[y]) {
    const init = {};
    for (let i = closed; i < 12; i++) init[i] = tradeRowCaMonth(d, c, r, y, i);
    r.caM[y] = init;                   // 1re saisie mensuelle : fige les autres mois
  }
  r.caM[y][m] = Number(v) || 0;
};
export const tradeLag = (r) => Math.min(3, Math.max(0, Math.round(Number(r.lag) || 0)));
/* Achat d'un mois (kCHF, magnitude) : la vente de M+lag au coût d'achat. */
export const tradeRowPurchaseMonth = (model, card, r, y, m) => {
  const mm = m + tradeLag(r);
  return tradeRowCaMonth(model, card, r, y + Math.floor(mm / 12), mm % 12)
    * (1 - (Number(r.margin) || 0) / 100);
};
export const tradeCardPurchaseMonth = (model, card, y, m) =>
  (card.rows || []).reduce((t, r) => t + tradeRowPurchaseMonth(model, card, r, y, m), 0);
export const tradePurchasesMonth = (model, y, m) =>
  productPlanOf(model).filter((c) => c.kind === 'trade')
    .reduce((s, c) => s + tradeCardPurchaseMonth(model, c, y, m), 0);

/* Prix effectif d'un produit : ± % d'augmentation ANNUELLE (r.index) appliqué
   dès N+1 — le coût des produits reste tel quel (l'escalade des coûts se
   saisit sur le coût lui-même si besoin). */
export const productRowPriceAt = (model, r, y) => {
  const p0 = Number(r.price) || 0;
  const idx = Number(r.index) || 0;
  const n = y - (model.currentYear || y);
  return n > 0 && idx ? p0 * (1 + idx / 100) ** n : p0;
};

/* Totaux par ligne de CA Finance (kCHF, comme le reste du plan). */
export const productPlanRevenueMonth = (model, lineId, y, m) =>
  productPlanOf(model).filter((c) => c.lineId === lineId)
    .reduce((s, c) => s + productCardRevenueMonth(model, c, y, m), 0);
export const productPlanCostMonth = (model, lineId, y, m) =>
  // Les cards négoce sont EXCLUES : leurs achats vont directement sur la ligne
  // Achats (tradePurchases), pas dans le coût des produits vendus (stock).
  productPlanOf(model).filter((c) => c.lineId === lineId && c.kind !== 'trade').reduce((s, c) =>
    s + c.rows.reduce((t, r) => t + productRowQtyMonth(r, y, m) * (Number(r.cost) || 0) / 1000, 0), 0);
/* Lignes de CA POSSÉDÉES par le Plan produits (verrouillées au Plan des ventes). */
export const productPlanLines = (model) =>
  Array.from(new Set(productPlanOf(model).map((c) => c.lineId).filter(Boolean)));

/* — PLAN SERVICES — même gabarit card que le Plan produits, mais chaque card
   porte un MODÈLE (`kind`) qui définit sa saisie et son moteur :
     · regie     — volumes × tarif (unité jour-homme ou heure) ;
     · capacity  — CA annuel par EPT (÷ 12) × EPT productifs du mois ;
     · recurring — prix mensuel × quantité TENUE (mandat, abonnement, location :
                   la quantité saisie un mois court jusqu'à la prochaine saisie) ;
     · prepaid   — service préfacturé : montant étalé sur `period` mois depuis
                   la facturation (billY/billM) — l'ACOMPTE cash viendra du
                   lot Finance ;
     · hourbank  — carnet d'heures : un TYPE de carnet par ligne (heures par
                   carnet, TARIF HORAIRE effectif, durée moyenne en mois) ;
                   on saisit le NOMBRE de carnets vendus par mois — facturé =
                   carnets × heures × tarif horaire (cash à la facturation,
                   comme le préfacturé), CA réparti sur la durée moyenne ;
     · project   — valeur totale × % de réalisation par mois (Σ ≤ 100),
                   coût en % de la valeur réalisée (costPct).
   Chaque card : compte de CA attribué (lineId), CONDITIONS DE PAIEMENT en jours
   (`terms`, champ enregistré — la connexion créances viendra du lot Finance),
   INDEXATION pluriannuelle (`index`, ± %/an appliqué aux tarifs dès N+1).
   Coûts : externes uniquement (les collaborateurs sont déjà comptés via
   People) — même règle d'affichage que le CA, vers cogsByLine. */
export const servicePlanOf = (model) => (model.servicePlan && model.servicePlan.cards) || [];
export const findServiceCard = (d, cid) => servicePlanOf(d).find((c) => c.id === cid);
export const addServiceCard = (d, kind, unit = '') => {
  if (!d.servicePlan) d.servicePlan = { cards: [] };
  // Prestations (fusion Régie / Projet / Contrat annuel, décision 2026-08-15) :
  // unité h|jh|chf + mode de saisie month|env + conversion h/jour (affichage).
  if (kind === 'presta') {
    const c = { id: newId(), kind, unit: unit || 'h', entry: 'month', hPerDay: 8,
      name: '', lineId: '', terms: 30, index: 0, rows: [] };
    d.servicePlan.cards.push(c);
    return c;
  }
  const c = { id: newId(), kind, unit, name: '', lineId: '', terms: 30, index: 0, rows: [] };
  // Carnet d'heures : mode de saisie card-level (qty | chf | env), comme la
  // pilule des prestations (demande 2026-08-16).
  // Carnet d'heures : unité HEURES par défaut (c'est sa nature) + mode de
  // saisie card-level (qty | chf | env), comme les prestations (2026-08-16).
  if (kind === 'hourbank') { c.entry = 'qty'; c.unit = 'h'; }
  // Préfacturé : unité FORFAIT (les autres modèles sont aussi des contrats),
  // mode de saisie Qté | CHF | % (2026-08-16).
  if (kind === 'prepaid') { c.entry = 'chf'; c.unit = 'pkg'; }
  // Récurrent / location : on compte des CONTRATS en cours (quantité tenue),
  // modes Qté (défaut) | CHF | % — 2026-08-16.
  if (kind === 'recurring') c.entry = 'qty';
  d.servicePlan.cards.push(c);
  return c;
};
/* Réorganisation des cards (drag & drop, 2026-08-16) : insère `cid` à la place
   de `beforeCid` (ou en fin si absent). */
export const moveServiceCard = (d, cid, beforeCid) => {
  const cards = servicePlanOf(d);
  const i = cards.findIndex((c) => c.id === cid);
  if (i < 0) return;
  const [c] = cards.splice(i, 1);
  const j = beforeCid ? cards.findIndex((x) => x.id === beforeCid) : -1;
  if (j < 0) cards.push(c); else cards.splice(j, 0, c);
};
export const delServiceCard = (d, cid) => {
  if (d.servicePlan) d.servicePlan.cards = servicePlanOf(d).filter((c) => c.id !== cid);
};
export const setServiceCardField = (d, cid, field, v) => {
  const c = findServiceCard(d, cid);
  if (c) c[field] = v;
};
export const addServiceRow = (d, cid) => {
  const c = findServiceCard(d, cid);
  if (!c) return null;
  const r = { id: newId(), name: '', cost: 0 };
  if (c.kind === 'regie') Object.assign(r, { price: 0, qty: {}, qtyM: {} });
  else if (c.kind === 'capacity') Object.assign(r, { annual: 0, qtyM: {} });
  else if (c.kind === 'recurring') Object.assign(r, { price: 0, qtyM: {}, valM: {}, value: {} });
  else if (c.kind === 'prepaid') Object.assign(r, { billedM: {}, period: 12, costPct: 0, value: {}, qtyM: {} });
  else if (c.kind === 'hourbank') Object.assign(r, { hours: 0, price: 0, period: 6, costPct: 0, qtyM: {} });
  else if (c.kind === 'annualvol') Object.assign(r, { volume: {}, price: 0, pctM: {} });
  else if (c.kind === 'project') Object.assign(r, { value: 0, costPct: 0, pct: {} });
  c.rows.push(r);
  return r;
};
export const delServiceRow = (d, cid, rid) => {
  const c = findServiceCard(d, cid);
  if (c) c.rows = c.rows.filter((r) => r.id !== rid);
};
export const setServiceRowField = (d, cid, rid, field, v) => {
  const c = findServiceCard(d, cid);
  const r = c && c.rows.find((x) => x.id === rid);
  if (r) r[field] = v;
};
/* Volumes de RÉGIE : mêmes conventions que les produits (annuel réparti,
   mensuel prioritaire, 1re saisie mensuelle fige les autres mois). */
export const setServiceRowQtyYear = (d, cid, rid, y, v) => {
  const c = findServiceCard(d, cid);
  const r = c && c.rows.find((x) => x.id === rid);
  if (!r) return;
  if (!r.qty) r.qty = {};
  r.qty[y] = Number(v) || 0;
  if (r.qtyM) delete r.qtyM[y];
};
export const setServiceRowQtyMonth = (d, cid, rid, y, m, v) => {
  const c = findServiceCard(d, cid);
  const r = c && c.rows.find((x) => x.id === rid);
  if (!r) return;
  if (!r.qtyM) r.qtyM = {};
  if (!r.qtyM[y]) {
    const init = {};
    for (let i = 0; i < 12; i++) init[i] = productRowQtyMonth(r, y, i);
    r.qtyM[y] = init;
  }
  r.qtyM[y][m] = Number(v) || 0;
};
/* Quantité TENUE (recurring, capacity) : la valeur d'un mois = la dernière
   saisie ≤ ce mois DANS L'ANNÉE ; sans saisie dans l'année, le niveau annuel
   (qty[y]) sert d'assiette — saisir janvier pour démarrer une année mensuelle. */
export const heldQtyMonth = (r, y, m) => {
  const qm = r.qtyM && r.qtyM[y];
  if (qm) {
    for (let i = m; i >= 0; i--) if (qm[i] != null && qm[i] !== '') return Number(qm[i]) || 0;
  }
  return Number(r.qty && r.qty[y]) || 0;
};
export const setHeldQtyMonth = (d, cid, rid, y, m, v) => {
  const c = findServiceCard(d, cid);
  const r = c && c.rows.find((x) => x.id === rid);
  if (!r) return;
  if (!r.qtyM) r.qtyM = {};
  if (!r.qtyM[y]) r.qtyM[y] = {};
  r.qtyM[y][m] = Number(v) || 0;      // la tenue fait le reste : pas d'étalement figé
};
export const setHeldQtyYear = (d, cid, rid, y, v) => {
  const c = findServiceCard(d, cid);
  const r = c && c.rows.find((x) => x.id === rid);
  if (!r) return;
  if (!r.qty) r.qty = {};
  r.qty[y] = Number(v) || 0;
  if (r.qtyM) delete r.qtyM[y];
};
/* PRÉFACTURÉ : la FACTURATION se saisit au mois (kCHF — ce qu'on facture,
   quand on le facture) ; le CA se RÉPARTIT sur `period` mois à partir de
   chaque facturation, le coût externe en % du facturé suit la même
   répartition. */
export const setPrepaidBilledMonth = (d, cid, rid, y, m, v) => {
  const c = findServiceCard(d, cid);
  const r = c && c.rows.find((x) => x.id === rid);
  if (!r) return;
  if (!r.billedM) r.billedM = {};
  if (!r.billedM[y]) r.billedM[y] = {};
  r.billedM[y][m] = Number(v) || 0;
};
/* Mode de saisie d'un PRÉFACTURÉ (card-level) — même grammaire que le carnet :
     · qty — on vend des forfaits : valeur/forfait · durée ;
       la cellule du mois = NOMBRE de forfaits facturés ;
     · chf — on facture un montant (défaut) : durée ; cellule = montant ;
     · env — enveloppe annuelle : CHF/an · durée ; cellule = % de l'enveloppe. */
export const prepaidEntry = (card) => card.entry || 'chf';
/* Mode de saisie d'un RÉCURRENT : qty (quantité TENUE × prix mensuel, défaut) ·
   chf (montant du mois) · env (enveloppe annuelle répartie en %). */
/* Mode de saisie d'une catégorie de PRODUITS : qty (unités, défaut) ou chf
   (chiffre d'affaires du mois, reconverti en unités via le prix) — même
   grammaire que les services (2026-08-16). */
/* Réorganisation des catégories de produits (drag & drop). */
export const moveProductCard = (d, cid, beforeCid) => {
  const cards = productPlanOf(d);
  const i = cards.findIndex((c) => c.id === cid);
  if (i < 0) return;
  const [c] = cards.splice(i, 1);
  const j = beforeCid ? cards.findIndex((x) => x.id === beforeCid) : -1;
  if (j < 0) cards.push(c); else cards.splice(j, 0, c);
};
export const productEntry = (card) => card.entry || 'qty';
/* Bascule Qté ↔ kCHF : les unités restent la donnée stockée, le CA en est la
   vue (unités × prix) — rien n'est perdu. */
export const convertProductCard = (d, cid, target) => {
  const c = productPlanOf(d).find((x) => x.id === cid);
  if (c && productEntry(c) !== target) c.entry = target;
};

export const recurringEntry = (card) => card.entry || 'qty';
export const recurringValMonth = (r, y, m) => Number(r.valM?.[y]?.[m]) || 0;
export const setRecurringValMonth = (d, cid, rid, y, m, v) => {
  const c = findServiceCard(d, cid);
  const r = c && c.rows.find((x) => x.id === rid);
  if (!r) return;
  if (!r.valM) r.valM = {};
  if (!r.valM[y]) r.valM[y] = {};
  r.valM[y][m] = Number(v) || 0;
};
/* Revenu d'un mois (kCHF) selon le mode — socle commun. */
export const recurringRevenueAt = (model, card, r, y, m) => {
  const f = idxFactor(model, card, y);
  const e = recurringEntry(card);
  if (e === 'chf') return recurringValMonth(r, y, m) * f / 1000;
  if (e === 'env') return (Number(r.value?.[y]) || 0) * (annualVolPct(r, y, m) / 100) * f / 1000;
  return heldQtyMonth(r, y, m) * (Number(r.price) || 0) * f / 1000;
};
/* Bascule de mode : le revenu mensuel est préservé. */
export const convertRecurringCard = (d, cid, target) => {
  const c = findServiceCard(d, cid);
  if (!c || c.kind !== 'recurring' || recurringEntry(c) === target) return;
  const years = yearsOf(d);
  (c.rows || []).forEach((r) => {
    const vals = {};
    years.forEach((y) => {
      vals[y] = Array.from({ length: 12 }, (_, m) => recurringRevenueAt(d, c, r, y, m) * 1000);
    });
    if (target === 'chf') {
      r.valM = {};
      years.forEach((y) => { r.valM[y] = {}; vals[y].forEach((v, m) => { if (v) r.valM[y][m] = v; }); });
      delete r.qtyM; delete r.qty; delete r.value; delete r.pctM;
    } else if (target === 'env') {
      r.value = {}; r.pctM = {};
      years.forEach((y) => {
        const tot = vals[y].reduce((a, b) => a + b, 0);
        if (!tot) return;
        r.value[y] = tot;
        r.pctM[y] = {};
        vals[y].forEach((v, m) => { if (v) r.pctM[y][m] = (v / tot) * 100; });
      });
      delete r.qtyM; delete r.qty; delete r.valM;
    } else {
      const price = Number(r.price) || 0;
      r.qtyM = {};
      if (price) {
        years.forEach((y) => {
          r.qtyM[y] = {};
          vals[y].forEach((v, m) => { r.qtyM[y][m] = v / price; });
        });
      }
      delete r.valM; delete r.value; delete r.pctM;
    }
  });
  c.entry = target;
};
export const prepaidQtyMonth = (r, y, m) => Number(r.qtyM?.[y]?.[m]) || 0;
export const setPrepaidQtyMonth = (d, cid, rid, y, m, v) => {
  const c = findServiceCard(d, cid);
  const r = c && c.rows.find((x) => x.id === rid);
  if (!r) return;
  if (!r.qtyM) r.qtyM = {};
  if (!r.qtyM[y]) r.qtyM[y] = {};
  r.qtyM[y][m] = Number(v) || 0;
};
/* Facturation d'un mois (CHF) selon le mode — socle commun aux trois. */
/* Facturation d'un mois en kCHF (unité historique de `billedM`) : valeur du
   forfait et enveloppe annuelle sont stockées en CHF. */
export const prepaidBilledAt = (card, r, y, m) => {
  const e = prepaidEntry(card);
  if (e === 'qty') return prepaidQtyMonth(r, y, m) * (Number(r.unitValue) || 0) / 1000;
  if (e === 'env') return (Number(r.value?.[y]) || 0) * (annualVolPct(r, y, m) / 100) / 1000;
  return Number(r.billedM?.[y]?.[m]) || 0;
};
/* Bascule de mode : la FACTURATION est préservée. */
export const convertPrepaidCard = (d, cid, target) => {
  const c = findServiceCard(d, cid);
  if (!c || c.kind !== 'prepaid') return;
  if (prepaidEntry(c) === target) return;
  const years = yearsOf(d);
  (c.rows || []).forEach((r) => {
    const vals = {};
    years.forEach((y) => { vals[y] = Array.from({ length: 12 }, (_, m) => prepaidBilledAt(c, r, y, m)); });
    if (target === 'chf') {
      r.billedM = {};
      years.forEach((y) => { r.billedM[y] = {}; vals[y].forEach((v, m) => { if (v) r.billedM[y][m] = v; }); });
      delete r.qtyM; delete r.value; delete r.pctM;
    } else if (target === 'env') {
      r.value = {}; r.pctM = {};
      years.forEach((y) => {
        const tot = vals[y].reduce((a, b) => a + b, 0);
        if (!tot) return;
        r.value[y] = tot * 1000;   // enveloppe stockée en CHF
        r.pctM[y] = {};
        vals[y].forEach((v, m) => { if (v) r.pctM[y][m] = (v / tot) * 100; });
      });
      delete r.qtyM; delete r.billedM;
    } else {
      const uv = (Number(r.unitValue) || 0) / 1000;   // kCHF par forfait
      r.qtyM = {};
      if (uv) years.forEach((y) => { r.qtyM[y] = {}; vals[y].forEach((v, m) => { if (v) r.qtyM[y][m] = v / uv; }); });
      delete r.billedM; delete r.value; delete r.pctM;
    }
  });
  c.entry = target;
};

export const prepaidBilledMonth = (r, y, m) => Number(r.billedM?.[y]?.[m]) || 0;
const prepaidSpreadMonth = (card, r, y, m) => {
  const period = Math.max(1, Number(r.period) || 1);
  let s = 0;
  for (let j = 0; j < period; j++) {
    const mm = m - j;
    const y2 = y + Math.floor(mm / 12);
    const m2 = ((mm % 12) + 12) % 12;
    s += prepaidBilledAt(card, r, y2, m2) / period;
  }
  return s;
};

/* CARNET D'HEURES : nombre de carnets vendus saisi par mois ; facturé =
   carnets × heures par carnet × tarif horaire effectif (l'indexation suit
   l'année de VENTE) ; le CA se répartit sur la durée moyenne (`period`),
   à cheval sur les années. */
export const setHourbankQtyMonth = (d, cid, rid, y, m, v) => {
  const c = findServiceCard(d, cid);
  const r = c && c.rows.find((x) => x.id === rid);
  if (!r) return;
  if (!r.qtyM) r.qtyM = {};
  if (!r.qtyM[y]) r.qtyM[y] = {};
  r.qtyM[y][m] = Number(v) || 0;
};
export const hourbankQtyMonth = (r, y, m) => Number(r.qtyM?.[y]?.[m]) || 0;
/* Mode de saisie du carnet (card-level) — refonte 2026-08-16, chaque mode a
   SES paramètres, on n'en affiche pas d'autres :
     · qty — on vend des carnets : h/carnet · tarif · durée ;
       la cellule du mois = NOMBRE de carnets (r.qtyM) ;
     · chf — on vend un montant : tarif · durée ;
       la cellule du mois = VALEUR facturée en CHF (r.valM) ;
     · env — enveloppe annuelle : valeur/an · tarif ;
       la cellule du mois = % de cette enveloppe (r.pctM, valeur dans r.value).
   Dans les trois cas, HEURES VENDUES = valeur facturée ÷ tarif horaire. */
export const hourbankEntry = (card) => card.entry || 'qty';
export const hourbankValMonth = (r, y, m) => Number(r.valM?.[y]?.[m]) || 0;
export const setHourbankValMonth = (d, cid, rid, y, m, v) => {
  const c = findServiceCard(d, cid);
  const r = c && c.rows.find((x) => x.id === rid);
  if (!r) return;
  if (!r.valM) r.valM = {};
  if (!r.valM[y]) r.valM[y] = {};
  r.valM[y][m] = Number(v) || 0;
};
export const setHourbankValYear = (d, cid, rid, y, v) => {
  const c = findServiceCard(d, cid);
  const r = c && c.rows.find((x) => x.id === rid);
  if (!r) return;
  if (!r.value) r.value = {};
  r.value[y] = Number(v) || 0;
};
export const hourbankQtyAt = (card, r, y, m) => (hourbankEntry(card) === 'qty'
  ? hourbankQtyMonth(r, y, m) : 0);
/* Valeur FACTURÉE d'un mois, en kCHF — socle commun aux trois modes. */
export const hourbankBilledMonth = (model, card, r, y, m) => {
  const idx = idxFactor(model, card, y);
  const e = hourbankEntry(card);
  if (e === 'chf') return hourbankValMonth(r, y, m) * idx / 1000;
  if (e === 'env') return (Number(r.value?.[y]) || 0) * (annualVolPct(r, y, m) / 100) * idx / 1000;
  return hourbankQtyMonth(r, y, m) * (Number(r.hours) || 0) * (Number(r.price) || 0) * idx / 1000;
};
const hourbankSpreadMonth = (model, card, r, y, m) => {
  const period = Math.max(1, Number(r.period) || 1);
  let s = 0;
  for (let j = 0; j < period; j++) {
    const mm = m - j;
    const y2 = y + Math.floor(mm / 12);
    const m2 = ((mm % 12) + 12) % 12;
    s += hourbankBilledMonth(model, card, r, y2, m2) / period;
  }
  return s;
};

/* VOLUME ANNUEL (contrat d'heures, LSE) : un volume convenu par année × un
   tarif horaire ; chaque mois porte un % DU VOLUME MENSUALISÉ (défaut 100 % =
   volume ÷ 12 — un été à 80 % réduit le mois sans toucher le contrat). */
export const setServiceRowVolYear = (d, cid, rid, y, v) => {
  const c = findServiceCard(d, cid);
  const r = c && c.rows.find((x) => x.id === rid);
  if (!r) return;
  if (!r.volume) r.volume = {};
  r.volume[y] = Number(v) || 0;
};
export const setServiceRowPctMonth = (d, cid, rid, y, m, v) => {
  const c = findServiceCard(d, cid);
  const r = c && c.rows.find((x) => x.id === rid);
  if (!r) return;
  if (!r.pctM) r.pctM = {};
  if (!r.pctM[y]) r.pctM[y] = {};
  r.pctM[y][m] = Number(v) || 0;
};
export const annualVolPct = (r, y, m) => {
  const p = r.pctM && r.pctM[y] && r.pctM[y][m];
  return p == null || p === '' ? 0 : Number(p) || 0;
};
export const annualVolPctTotal = (r, y) => {
  const yy = r.pctM && r.pctM[y];
  return yy ? Object.values(yy).reduce((s, x) => s + (Number(x) || 0), 0) : 0;
};

/* % de réalisation d'un projet : par année × mois (clé `pct[y][m]`, en %). */
export const setProjectPct = (d, cid, rid, y, m, v) => {
  const c = findServiceCard(d, cid);
  const r = c && c.rows.find((x) => x.id === rid);
  if (!r) return;
  if (!r.pct) r.pct = {};
  if (!r.pct[y]) r.pct[y] = {};
  r.pct[y][m] = Number(v) || 0;
};
export const projectPctTotal = (r) =>
  Object.values(r.pct || {}).reduce((s, yy) =>
    s + Object.values(yy).reduce((t, x) => t + (Number(x) || 0), 0), 0);
/* Indexation pluriannuelle : facteur appliqué aux tarifs dès N+1. */
export const idxFactor = (model, card, y) => {
  const p = Number(card.index) || 0;
  const n = y - (model.currentYear || y);
  return n > 0 && p ? (1 + p / 100) ** n : 1;
};
/* CA d'une LIGNE de service sur un mois (kCHF). */
/* Mode de saisie d'une LIGNE presta : « au mois » ou « enveloppe % » — PAR
   ligne (demande 2026-08-15 : dans une même card, SIG se saisit au mois et la
   grosse commande en %). `card.entry` reste le défaut des lignes sans choix. */
export const prestaRowEnv = (card, r) =>
  (r.entry ? r.entry === 'env' : (card.entry || 'month') === 'env');
/* Valeur d'un mois en CHF (volume × tarif indexé) — vue « Valeur ». */
export const prestaValueMonth = (model, card, r, y, m) =>
  productRowQtyMonth(r, y, m) * (Number(r.price) || 0) * idxFactor(model, card, y);

/* CONVERSION d'une card presta au changement d'option de la pilule
   (Heures | Jours-hommes | %) — les valeurs saisies sont CONVERTIES, jamais
   perdues (demande 2026-08-15) :
     · h ↔ jh : volumes ÷/× hPerDay, tarif et coût ×/÷ hPerDay (CA invariant) ;
     · → %    : chaque ligne « au mois » devient une enveloppe — volume total
                de l'année + % par mois recalculés depuis les volumes saisis ;
     · % → h/jh : l'enveloppe redevient des volumes mensuels (vol × %). */
/* Pilules SÉPARÉES (2026-08-16) : « Unité » (heures | jours-hommes) et
   « Saisie » (Qté | %) — avant, le % se confondait avec une unité alors qu'il
   ne dit QUE le mode de répartition ; le volume reste en heures ou en JH.
   L'unité se change SANS quitter le mode de saisie (et inversement). */
/* Valeur d'UN carnet (contenu × tarif), en CHF — l'indicateur qui manquait. */
export const hourbankValue = (r) => (Number(r.hours) || 0) * (Number(r.price) || 0);

export const setPrestaUnit = (d, cid, unit) => {
  const c = findServiceCard(d, cid);
  if (!c || c.kind !== 'presta' || c.unit === unit) return;
  const hpd = Number(c.hPerDay) || 8;
  const vf = (c.unit === 'h' && unit === 'jh') ? 1 / hpd : (c.unit === 'jh' && unit === 'h') ? hpd : 1;
  if (vf !== 1) {
    (c.rows || []).forEach((r) => {
      if (r.qtyM) Object.keys(r.qtyM).forEach((y) => Object.keys(r.qtyM[y]).forEach((m) => { r.qtyM[y][m] = (Number(r.qtyM[y][m]) || 0) * vf; }));
      if (r.qty) Object.keys(r.qty).forEach((y) => { r.qty[y] = (Number(r.qty[y]) || 0) * vf; });
      if (r.volume) Object.keys(r.volume).forEach((y) => { r.volume[y] = (Number(r.volume[y]) || 0) * vf; });
      if (r.price != null) r.price = (Number(r.price) || 0) / vf;
      if (r.cost != null) r.cost = (Number(r.cost) || 0) / vf;
    });
  }
  c.unit = unit;
};
/* Mode de saisie : « Qté » (volumes au mois) ou « % » (enveloppe annuelle). */
export const setPrestaEntry = (d, cid, entry) => {
  const c = findServiceCard(d, cid);
  if (!c || c.kind !== 'presta') return;
  // « Valeur » (CHF) est une VUE des volumes (volume × tarif) : le stockage
  // reste le volume — comme pour le carnet d'heures (2026-08-16).
  convertPrestaCard(d, cid, entry === 'env' ? 'env' : (c.unit === 'chf' ? 'h' : c.unit));
  if (entry !== 'env') c.entry = entry;   // 'month' | 'chf'
};
/* Mode de saisie effectif d'une ligne presta : 'month' | 'chf' | 'env'. */
export const prestaEntry = (card) => card.entry || 'month';

export const convertPrestaCard = (d, cid, target) => {
  const c = findServiceCard(d, cid);
  if (!c || c.kind !== 'presta') return;
  const hpd = Number(c.hPerDay) || 8;
  const years = yearsOf(d);
  const toEnv = target === 'env';
  const unitTarget = toEnv ? (c.unit === 'chf' ? 'h' : c.unit) : target;
  // Facteur volumes unité actuelle → cible (h→jh : ÷ hpd ; jh→h : × hpd).
  const vf = (c.unit === 'h' && unitTarget === 'jh') ? 1 / hpd
    : (c.unit === 'jh' && unitTarget === 'h') ? hpd : 1;
  (c.rows || []).forEach((r) => {
    const wasEnv = prestaRowEnv(c, r);
    // 1. Conversion d'unité (volumes + tarifs, CA invariant).
    if (vf !== 1) {
      if (r.qtyM) Object.keys(r.qtyM).forEach((y) => Object.keys(r.qtyM[y]).forEach((m) => { r.qtyM[y][m] = (Number(r.qtyM[y][m]) || 0) * vf; }));
      if (r.qty) Object.keys(r.qty).forEach((y) => { r.qty[y] = (Number(r.qty[y]) || 0) * vf; });
      if (r.volume) Object.keys(r.volume).forEach((y) => { r.volume[y] = (Number(r.volume[y]) || 0) * vf; });
      if (r.price != null) r.price = (Number(r.price) || 0) / vf;
      if (r.cost != null) r.cost = (Number(r.cost) || 0) / vf;
    }
    // 2. Bascule de mode, valeurs converties.
    if (toEnv && !wasEnv) {
      years.forEach((y) => {
        const tot = productRowQtyYear(r, y) * (vf !== 1 ? 1 : 1);
        if (!tot) return;
        if (!r.volume) r.volume = {};
        r.volume[y] = tot;
        if (isMonthlyYear(d, y)) {
          if (!r.pctM) r.pctM = {};
          r.pctM[y] = {};
          for (let m = 0; m < 12; m++) {
            const q = productRowQtyMonth(r, y, m);
            // Précision pleine : l'invariance du CA prime sur un % arrondi
            // (l'affichage arrondit déjà à la décimale).
            if (q) r.pctM[y][m] = (q / tot) * 100;
          }
        }
      });
      delete r.qtyM; delete r.qty;
      r.entry = 'env';
    } else if (!toEnv && wasEnv) {
      years.forEach((y) => {
        const vol = Number(r.volume?.[y]) || 0;
        if (!vol) return;
        if (isMonthlyYear(d, y)) {
          if (!r.qtyM) r.qtyM = {};
          r.qtyM[y] = {};
          for (let m = 0; m < 12; m++) {
            const p = annualVolPct(r, y, m);
            if (p) r.qtyM[y][m] = vol * p / 100;
          }
        } else {
          if (!r.qty) r.qty = {};
          r.qty[y] = vol;
        }
      });
      delete r.volume; delete r.pctM;
      r.entry = 'month';
    } else {
      r.entry = toEnv ? 'env' : 'month';
    }
  });
  c.unit = unitTarget;
  c.entry = toEnv ? 'env' : 'month';
};

/* CONVERSION d'une card carnet d'heures au changement de pilule
   (Qté (carnets) | Valeur | %) — valeurs converties, jamais perdues :
     · qty ↔ chf : rien à convertir (la valeur est une VUE des carnets :
       facturé = carnets × heures × tarif — la saisie chf est retransformée
       en carnets) ;
     · → %      : les carnets saisis deviennent une enveloppe annuelle
       (volume total + % par mois, précision pleine) ;
     · % → qty/chf : l'enveloppe redevient des carnets mensuels. */
export const convertHourbankCard = (d, cid, target) => {
  const c = findServiceCard(d, cid);
  if (!c || c.kind !== 'hourbank') return;
  const was = hourbankEntry(c);
  if (was === target) return;
  const years = yearsOf(d);
  (c.rows || []).forEach((r) => {
    // 1. Valeur facturée par mois AVANT bascule (kCHF → CHF).
    const vals = {};
    years.forEach((y) => {
      vals[y] = Array.from({ length: 12 }, (_, m) => hourbankBilledMonth(d, c, r, y, m) * 1000);
    });
    // 2. Réécriture dans le format du mode cible.
    if (target === 'chf') {
      r.valM = {};
      years.forEach((y) => { r.valM[y] = {}; vals[y].forEach((v, m) => { if (v) r.valM[y][m] = v; }); });
      delete r.qtyM; delete r.value; delete r.pctM;
    } else if (target === 'env') {
      r.value = {}; r.pctM = {};
      years.forEach((y) => {
        const tot = vals[y].reduce((a, b) => a + b, 0);
        if (!tot) return;
        r.value[y] = tot;
        r.pctM[y] = {};
        vals[y].forEach((v, m) => { if (v) r.pctM[y][m] = (v / tot) * 100; });
      });
      delete r.qtyM; delete r.valM;
    } else {
      // Retour aux carnets : nombre = valeur ÷ (contenu × tarif).
      const unitVal = (Number(r.hours) || 0) * (Number(r.price) || 0);
      r.qtyM = {};
      if (unitVal) {
        years.forEach((y) => {
          r.qtyM[y] = {};
          vals[y].forEach((v, m) => { if (v) r.qtyM[y][m] = v / unitVal; });
        });
      }
      delete r.valM; delete r.value; delete r.pctM;
    }
  });
  c.entry = target;
};


/* HEURES DE TRAVAIL VENDUES d'une card (puis du plan) — l'indicateur de volume
   qui CUMULE carnets d'heures et prestations (demande 2026-08-16) :
     · hourbank  — carnets × heures/carnet ;
     · presta    — volumes h (jh × h/jour) ; unité CHF = pas d'heures ;
     · regie / annualvol (legacy) — mêmes conventions. */
export const serviceCardHoursMonth = (model, card, y, m) => {
  const rows = card.rows || [];
  if (card.kind === 'hourbank') {
    // Heures vendues = valeur facturée ÷ tarif horaire (vrai dans les 3 modes).
    return rows.reduce((s, r) => {
      const p = Number(r.price) || 0;
      if (!p) return s;
      return s + (hourbankBilledMonth(model, card, r, y, m) * 1000) / p;
    }, 0);
  }
  if (card.kind === 'presta') {
    if (card.unit === 'chf') return 0;
    const f = card.unit === 'jh' ? (Number(card.hPerDay) || 8) : 1;
    return rows.reduce((s, r) => s + (prestaRowEnv(card, r)
      ? (Number(r.volume?.[y]) || 0) * annualVolPct(r, y, m) / 100
      : productRowQtyMonth(r, y, m)) * f, 0);
  }
  if (card.kind === 'regie') {
    const f = card.unit === 'jh' ? 8 : 1;
    return rows.reduce((s, r) => s + productRowQtyMonth(r, y, m) * f, 0);
  }
  if (card.kind === 'annualvol') {
    return rows.reduce((s, r) => s + (Number(r.volume?.[y]) || 0) * annualVolPct(r, y, m) / 100, 0);
  }
  return 0;
};
export const servicePlanHoursMonth = (model, y, m) =>
  servicePlanOf(model).reduce((s, c) => s + serviceCardHoursMonth(model, c, y, m), 0);

export const serviceRowRevenueMonth = (model, card, r, y, m) => {
  const f = idxFactor(model, card, y);
  if (card.kind === 'regie') return productRowQtyMonth(r, y, m) * (Number(r.price) || 0) * f / 1000;
  if (card.kind === 'capacity') return heldQtyMonth(r, y, m) * ((Number(r.annual) || 0) * f / 12) / 1000;
  if (card.kind === 'recurring') return recurringRevenueAt(model, card, r, y, m);
  if (card.kind === 'prepaid') return prepaidSpreadMonth(card, r, y, m);
  if (card.kind === 'hourbank') return hourbankSpreadMonth(model, card, r, y, m);
  if (card.kind === 'project') return (Number(r.value) || 0) * ((Number(r.pct?.[y]?.[m]) || 0) / 100) / 1000;
  if (card.kind === 'annualvol') {
    return (Number(r.volume?.[y]) || 0) * (annualVolPct(r, y, m) / 100) * (Number(r.price) || 0) * f / 1000;
  }
  /* PRESTATIONS (fusion 2026-08-15) — deux modes de saisie :
       · 'month' : volume du mois saisi directement (comme la régie) ;
       · 'env'   : ENVELOPPE — volume total de l'année × % du mois (comme le
                   contrat annuel — le cas « grosse commande sans idée du
                   rythme » : on connaît le total, pas la consommation).
     Unité 'chf' : le volume EST le CA en kCHF (pas de tarif) — couvre les
     projets à l'avancement. */
  if (card.kind === 'presta') {
    const vol = prestaRowEnv(card, r)
      ? (Number(r.volume?.[y]) || 0) * (annualVolPct(r, y, m) / 100)
      : productRowQtyMonth(r, y, m);
    if (card.unit === 'chf') return vol;
    return vol * (Number(r.price) || 0) * f / 1000;
  }
  return 0;
};
export const serviceRowCostMonth = (model, card, r, y, m) => {
  const f = idxFactor(model, card, y);
  if (card.kind === 'project') return serviceRowRevenueMonth(model, card, r, y, m) * ((Number(r.costPct) || 0) / 100);
  if (card.kind === 'regie') return productRowQtyMonth(r, y, m) * (Number(r.cost) || 0) * f / 1000;
  if (card.kind === 'capacity') return heldQtyMonth(r, y, m) * ((Number(r.cost) || 0) * f / 12) / 1000;
  if (card.kind === 'recurring') {
    // Coût : par quantité en mode Qté, en % du revenu dans les autres modes
    // (il n'y a alors plus de quantité — décision 2026-08-16).
    if (recurringEntry(card) === 'qty') return heldQtyMonth(r, y, m) * (Number(r.cost) || 0) * f / 1000;
    return recurringRevenueAt(model, card, r, y, m) * ((Number(r.costPct) || 0) / 100);
  }
  if (card.kind === 'prepaid') return prepaidSpreadMonth(card, r, y, m) * ((Number(r.costPct) || 0) / 100);
  if (card.kind === 'hourbank') return hourbankSpreadMonth(model, card, r, y, m) * ((Number(r.costPct) || 0) / 100);
  if (card.kind === 'annualvol') {
    return (Number(r.volume?.[y]) || 0) * (annualVolPct(r, y, m) / 100) * (Number(r.cost) || 0) * f / 1000;
  }
  // Prestations : coût unitaire (h/jh) ou % du CA reconnu (unité CHF).
  if (card.kind === 'presta') {
    if (card.unit === 'chf') {
      return serviceRowRevenueMonth(model, card, r, y, m) * ((Number(r.costPct) || 0) / 100);
    }
    const vol = prestaRowEnv(card, r)
      ? (Number(r.volume?.[y]) || 0) * (annualVolPct(r, y, m) / 100)
      : productRowQtyMonth(r, y, m);
    return vol * (Number(r.cost) || 0) * f / 1000;
  }
  return 0;
};
export const serviceCardRevenueMonth = (model, card, y, m) =>
  (card.rows || []).reduce((t, r) => t + serviceRowRevenueMonth(model, card, r, y, m), 0);
export const servicePlanRevenueMonth = (model, lineId, y, m) =>
  servicePlanOf(model).filter((c) => c.lineId === lineId)
    .reduce((s, c) => s + serviceCardRevenueMonth(model, c, y, m), 0);
/* Coût d'UNE card (kCHF) — le détail que la Valeur ajoutée planifiée affiche
   sous les charges directes : une ligne par card, produits et services
   (2026-08-16). */
export const serviceCardCostMonth = (model, card, y, m) =>
  (card.rows || []).reduce((t, r) => t + serviceRowCostMonth(model, card, r, y, m), 0);
export const productCardCostMonth = (model, card, y, m) =>
  (card.kind === 'trade' ? 0
    : (card.rows || []).reduce((t, r) => t + productRowQtyMonth(r, y, m) * (Number(r.cost) || 0) / 1000, 0));

export const servicePlanCostMonth = (model, lineId, y, m) =>
  servicePlanOf(model).filter((c) => c.lineId === lineId).reduce((s, c) =>
    s + (c.rows || []).reduce((t, r) => t + serviceRowCostMonth(model, c, r, y, m), 0), 0);
export const servicePlanLines = (model) =>
  Array.from(new Set(servicePlanOf(model).map((c) => c.lineId).filter(Boolean)));
/* Lignes possédées par un plan (produits OU services) : verrou au Plan des ventes. */
export const plannedLines = (model) =>
  Array.from(new Set([...productPlanLines(model), ...servicePlanLines(model)]));

/* Taux d'une RÈGLE (revenu variable) : par ANNÉE, TENU — l'année sans saisie
   reprend le dernier taux posé ; 0 explicite coupe la règle. */
export const heldVarRate = (v, y, y0) => {
  for (let yy = y; yy >= (y0 || y); yy--) {
    const r = v.rate && v.rate[yy];
    if (r != null && r !== '') return Number(r) || 0;
  }
  return 0;
};

/* CONDITIONS DE PAIEMENT PAR CARD (décision Olivier 2026-08-17) — la card dit
   à quel délai elle encaisse ; le DSO de la ligne Créances clients ne pilote
   plus que le CA qu'aucune card ne couvre.

   Deux notions à ne pas confondre :
     · FACTURATION — ce qui crée la créance. Pour le préfacturé et le carnet
       d'heures, la facture part en une fois et le CA s'étale ensuite : c'est la
       facture qui compte pour la trésorerie, pas le CA reconnu.
     · CA reconnu — ce que le compte de résultat porte (déjà joint par
       revenueByLine). On le retire du flux au DSO pour éviter le double compte. */

/** Délai d'encaissement d'une card, en jours. Préfacturé et carnet d'heures
 *  s'encaissent « à la facturation » (leur écran l'affiche ainsi) → 0 jour. */
export const cardTermsDays = (card) => (
  (card?.kind === 'prepaid' || card?.kind === 'hourbank') ? 0 : Math.max(0, Number(card?.terms) || 0)
);

/** Facturation d'une card sur un mois (kCHF) — base de la créance. */
export const cardBilledMonth = (model, card, svc, y, m) => {
  if (!svc) return productCardRevenueMonth(model, card, y, m);
  if (card.kind === 'prepaid') {
    return (card.rows || []).reduce((t, r) => t + prepaidBilledAt(card, r, y, m), 0);
  }
  if (card.kind === 'hourbank') {
    return (card.rows || []).reduce((t, r) => t + hourbankBilledMonth(model, card, r, y, m), 0);
  }
  return serviceCardRevenueMonth(model, card, y, m);
};

/** Poids d'encaissement d'un délai, mois par mois : [w0, w1, …] où w0 = part
 *  encaissée le mois de la facture. Même arithmétique 30/360 que le roulement
 *  des créances (countback) : à 45 jours, la moitié tombe à M+1, l'autre à M+2.
 *  Ainsi le plan et le bilan encaissent la même vente au même moment. */
export const collectionWeights = (days) => {
  const MD = 30;
  let rem = Math.max(0, Number(days) || 0);
  const unpaid = [];                       // part encore due, par mois de recul
  while (rem > 1e-9) {
    const take = Math.min(rem, MD);
    unpaid.push(take / MD);
    rem -= take;
  }
  const w = [];
  let prev = 1;
  unpaid.forEach((u) => { w.push(prev - u); prev = u; });
  w.push(prev);                            // le solde tombe le mois suivant
  return w;
};

/** Encaissement d'une card sur un mois (kCHF) : sa facturation décalée de ses
 *  conditions de paiement. Les mois d'avant janvier viennent de l'année N−1. */
export const cardCollectedMonth = (model, card, svc, y, m) =>
  collectionWeights(cardTermsDays(card)).reduce((s, w, j) => {
    if (!w) return s;
    const mm = m - j;
    const y2 = y + Math.floor(mm / 12);
    const m2 = ((mm % 12) + 12) % 12;
    return s + w * cardBilledMonth(model, card, svc, y2, m2);
  }, 0);

/** Toutes les cards attribuées à un compte de CA, produits et services. */
/* Une card N'EST un panier de facturation que si ses conditions de paiement
   sont EXPLICITES : préfacturé / carnet d'heures (structurellement comptant),
   ou un délai saisi (0 compris = comptant voulu). Sans condition saisie, la
   card reste dans le résiduel et suit le DSO de la ligne Créances — le défaut
   silencieux « comptant » encaissait ses ventes le mois même (constat
   Z-MEDTECH « Montre SE », 2026-08-20). */
const cardHasTerms = (c) => c?.kind === 'prepaid' || c?.kind === 'hourbank'
  || (c?.terms != null && String(c.terms).trim() !== '' && Number.isFinite(Number(c.terms)));
const billingCards = (model) => [
  ...productPlanOf(model).map((c) => ({ c, svc: false })),
  ...servicePlanOf(model).map((c) => ({ c, svc: true })),
].filter(({ c }) => c.lineId && cardHasTerms(c));

/* Jointures `computed` (totaux plan par ligne, CHF) — UNE implémentation
   partagée entre les pages Plan des ventes et Plan produits : la page qui sauve
   en dernier écrirait sinon une jointure calculée autrement. Le serveur ne lit
   que les mois OUVERTS ; sur les mois clos, l'écran du Plan des ventes peut
   afficher la compta (connexion Finance) à la place de ces valeurs — sans effet. */
export const joinedPlanByLine = (model, lineIds) => {
  const varsFor = (id) => variablesOf(model).filter((v) => parseRef(v.targetRef).lineId === id);
  const adjM = (id, y, m) => Number(monthsOf(adjPlanOf(model, id), y)[m]) || 0;
  // Ligne POSSÉDÉE par un plan (produits/services) : le plan pilote SEUL —
  // les anciennes saisies de la ligne, ses variables et son plan « Autres … »
  // sont écartés (sinon double compte à la connexion d'une card).
  const owned = new Set(plannedLines(model));
  const prodM = (id, y, m) => productPlanRevenueMonth(model, id, y, m)
    + servicePlanRevenueMonth(model, id, y, m);
  const baseMonthNv = (ref, y, m) => {
    const r = parseRef(ref);
    if (r.kind === 'det') {
      const dd = detailsOf(model, r.lineId).find((x) => x.id === r.did);
      return dd ? detailMonth(model, dd, y, m) : 0;
    }
    if (r.kind === 'adj') return 0;   // l'écart compta n'existe que connecté
    // Une ligne pilotée par le Plan produits sert de base avec son CA produits.
    if (owned.has(r.lineId)) return prodM(r.lineId, y, m);
    return lineMonth(model, r.lineId, y, m) + adjM(r.lineId, y, m) + prodM(r.lineId, y, m);
  };
  const varM = (id, y, m) => varsFor(id).reduce((s, v) =>
    s + heldVarRate(v, y, model.currentYear) / 100 * baseMonthNv(v.baseRef, y, m), 0);
  const out = { revenueByLine: {}, cogsByLine: {}, tradePurchases: {},
    // Conditions de paiement des cards : facturation PAR CARD (délai + série)
    // et CA reconnu par ces mêmes cards — le serveur en fait des paniers de
    // créances, le DSO ne garde que le résiduel. Par card et non par délai :
    // le plan de liquidité doit pouvoir montrer qui encaisse quoi (2026-08-17).
    arBillingByCard: {}, cardRevenue: {},
    // COÛT MATÉRIEL par LIGNE D'ACCUEIL (décision Olivier 2026-08-16) : les
    // colonnes de coût des cards ne portent que le matériel, et chaque card
    // désigne la ligne de charges directes qui le reçoit (`costLine`). Une
    // ligne peut cumuler plusieurs cards. Les cards négoce sont exclues
    // (leurs achats passent par le circuit négoce, sinon double compte).
    cogsByCostLine: {} };
  yearsOf(model).forEach((y) => {
    const rev = {};
    const cogs = {};
    (lineIds || []).forEach((id) => {
      const vals = Array.from({ length: 12 }, (_, m) => Math.round(
        (owned.has(id)
          // Le résiduel planifié « Autres … » complète les cards (2026-08-16).
          ? prodM(id, y, m) + adjM(id, y, m)
          : lineMonth(model, id, y, m) + varM(id, y, m) + adjM(id, y, m) + prodM(id, y, m)) * 1000));
      // Série TOUTE À ZÉRO CONSERVÉE (2026-08-19) : « planifié à zéro » est une
      // vérité du plan, pas une absence. En la retirant du document, le serveur
      // gardait l'ancienne prévision de la ligne (46 k fantômes sur Revenus
      // récurrents après une règle « Vide (0) »).
      rev[id] = vals;
      const cvals = Array.from({ length: 12 }, (_, m) =>
        Math.round((productPlanCostMonth(model, id, y, m)
          + servicePlanCostMonth(model, id, y, m)) * 1000));
      cogs[id] = cvals;
    });
    if (Object.keys(rev).length) out.revenueByLine[String(y)] = rev;
    if (Object.keys(cogs).length) out.cogsByLine[String(y)] = cogs;
    const byCost = {};
    [...productPlanOf(model).filter((c) => c.kind !== 'trade').map((c) => ({ c, svc: false })),
      ...servicePlanOf(model).map((c) => ({ c, svc: true }))].forEach(({ c, svc }) => {
      // Allocation portée par le PLAN (`cardCostLines`, clé possédée par la
      // Valeur ajoutée), pas par la card : les pages Plan produits / services
      // possèdent leurs cards, cette page ne doit pas les réécrire.
      const dest = (model.cardCostLines || {})[c.id];
      if (!dest) return;            // card non allouée : elle ne charge aucune ligne
      const vals = Array.from({ length: 12 }, (_, m) => Math.round(
        (svc ? serviceCardCostMonth(model, c, y, m) : productCardCostMonth(model, c, y, m)) * 1000));
      const cur = byCost[dest] || Array.from({ length: 12 }, () => 0);
      byCost[dest] = cur.map((v, i) => v + vals[i]);
    });
    if (Object.keys(byCost).length) out.cogsByCostLine[String(y)] = byCost;
    const bill = [];
    const cardRev = Array.from({ length: 12 }, () => 0);
    billingCards(model).forEach(({ c, svc }) => {
      const flow = Array.from({ length: 12 }, () => 0);
      for (let m = 0; m < 12; m += 1) {
        flow[m] = Math.round(cardBilledMonth(model, c, svc, y, m) * 1000);
        cardRev[m] += Math.round(
          (svc ? serviceCardRevenueMonth(model, c, y, m) : productCardRevenueMonth(model, c, y, m)) * 1000);
      }
      if (flow.some((v) => v !== 0)) {
        bill.push({ id: c.id, name: c.name || '', kind: c.kind || (svc ? 'service' : 'product'),
          days: cardTermsDays(c), flow });
      }
    });
    if (bill.length) {
      out.arBillingByCard[String(y)] = bill;
      out.cardRevenue[String(y)] = cardRev;
    }
    // Achats négoce (kCHF → CHF) : série UNIQUE toutes cards trade confondues —
    // le serveur la pose sur la ligne Achats (méthode sales_purchases).
    const tvals = Array.from({ length: 12 }, (_, m) => Math.round(tradePurchasesMonth(model, y, m) * 1000));
    if (tvals.some((v) => v !== 0)) out.tradePurchases[String(y)] = tvals;
  });
  return out;
};
